import { HttpErrorResponse } from '@angular/common/http';

// Assumed error envelope for domains with no real contract yet — only status +
// a suggested code per case. Fase 4 (design-handoff/fase4/planificacion_fase_4.md)
// is a real contract and its `error` is a bare string code with the message at
// the envelope's top level (`{"status":false,"data":null,"message":"...","error":"CODE"}`),
// not the nested `{code,message,fieldErrors}` object every other domain's mocks
// emit — both shapes are handled here rather than splitting into a parallel
// envelope module for one field's difference.
export interface ApiErrorBody {
  message?: string;
  error:
    | {
        code: string;
        message?: string;
        fieldErrors?: Record<string, string[]>;
      }
    | string
    | null;
}

export class ApiError<TCode extends string = string> extends Error {
  constructor(
    readonly code: TCode,
    readonly status: number,
    message: string,
    readonly fieldErrors?: Record<string, string[]>,
  ) {
    super(message);
  }
}

type ApiErrorConstructor<TCode extends string, TError extends ApiError<string>> = new (
  code: TCode,
  status: number,
  message: string,
  fieldErrors?: Record<string, string[]>,
) => TError;

// Shared by every domain service's catchError: pulls the code/message/field
// errors out of the assumed envelope above, falling back to a domain-supplied
// code when the body doesn't match. Pass a subclass (e.g. RegistrationApiError,
// whose code type is the union of every error enum it can throw) to preserve
// that type at the throw site; omit it to get a plain ApiError<TCode>.
export function toApiError<TCode extends string>(response: HttpErrorResponse, fallbackCode: TCode): ApiError<TCode>;
export function toApiError<TCode extends string, TError extends ApiError<string>>(
  response: HttpErrorResponse,
  fallbackCode: TCode,
  ErrorClass: ApiErrorConstructor<TCode, TError>,
): TError;
export function toApiError(
  response: HttpErrorResponse,
  fallbackCode: string,
  ErrorClass: ApiErrorConstructor<string, ApiError<string>> = ApiError,
): ApiError<string> {
  const body = response.error as ApiErrorBody | undefined;
  const errorField = body?.error;
  const isErrorObject = typeof errorField === 'object' && errorField !== null;
  const code = (isErrorObject ? errorField.code : errorField) ?? fallbackCode;
  const message = (isErrorObject ? errorField.message : undefined) ?? body?.message ?? 'Ocurrió un error inesperado.';
  const fieldErrors = isErrorObject ? errorField.fieldErrors : undefined;
  return new ErrorClass(code, response.status, message, fieldErrors);
}
